import Button from '@components/common/Button/Button';
import Image from '@components/common/Image/Image';
import Link from '@components/common/Link/Link';
import LanguageSwitcher from '@components/layout/LanguageSwitcher/LanguageSwitcher';
import LoginButton from '@components/layout/LoginButton/LoginButton';
import BurgerIcon from '@src/assets/icons/burger.svg';
import DropdownIcon from '@src/assets/icons/dropdown.svg';
import LogoIcon from '@src/assets/icons/logo-icon.svg';
import LogoPrimary from '@src/assets/icons/mokosi-primary.svg';
import LogoWhite from '@src/assets/icons/mokosi-white.svg';
import CartButton from '@src/cart/components/CartButton';
import { Dropdown, Option } from '@src/components/Dropdown';
import pageSlugs from '@src/components/layout/pageSlugs';
import { useUser } from '@src/services/userContext';
import type { Locale } from '@src/utils/locale';
import type { ImageDataLike } from 'gatsby-plugin-image';
import type { MouseEvent, ReactElement } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Flex } from 'theme-ui';
import './Navbar.scss';

const themes = {
  light: 'light',
  dark: 'dark',
};

enum NavbarTheme {
  dark = 'dark',
  light = 'light',
}

type NavbarProps = {
  supportBanner: ReactElement;
  infoPages: {
    items: {
      layout: string;
      slug: string;
      title: string;
    }[];
  };
  subjects: {
    count: number;
    icon: ImageDataLike;
    name: string;
    slug: string;
  }[];
  homePageSlug: string;
  languages: [];
  navTheme: NavbarTheme;
  onLoginIn: () => void;
  pageLocale: Locale;
};

const resolveTeachersSlug = (locale: Locale) => {
  return pageSlugs.teachers[locale];
};
const Navbar = ({
  subjects,
  pageLocale,
  homePageSlug,
  languages,
  onLoginIn,
  navTheme,
  infoPages,
  supportBanner,
}: NavbarProps) => {
  const [navbarOpen, setNavbarOpen] = useState(false);
  const { t } = useTranslation();
  const [theme, setTheme] = useState<NavbarTheme>(navTheme);
  const [subjectsOpen, setSubjectsOpen] = useState(false);
  const user = useUser();

  const [cUser, setCuser] = useState<any>(null);

  const toggleSubjectsMenu = () => {
    setSubjectsOpen(!subjectsOpen);
  };
  const closeSubjectMenu = () => {
    setSubjectsOpen(false);
  };

  const handleNavbarToggle = (event: MouseEvent) => {
    event.stopPropagation();
    setNavbarOpen(!navbarOpen);
  };

  const closeNavbar = () => {
    setNavbarOpen(false);
  };

  useEffect(() => {
    if (navbarOpen) {
      window.addEventListener('click', closeNavbar);
    } else {
      window.removeEventListener('click', closeNavbar);
    }

    if (subjectsOpen) {
      window.addEventListener('click', closeSubjectMenu);
    } else {
      window.removeEventListener('click', closeSubjectMenu);
    }

    return () => {
      window.removeEventListener('click', closeSubjectMenu);
      window.removeEventListener('click', closeNavbar);
    };
  }, [navbarOpen, subjectsOpen]);

  const checkOffset = () => {
    if (document) {
      if (
        document.scrollingElement?.scrollTop &&
        document.scrollingElement?.scrollTop > 150
      ) {
        document.body.classList.add('nav-scrolled');
        setTheme(NavbarTheme.dark);
      } else {
        document.body.classList.remove('nav-scrolled');
        setTheme(navTheme);
      }
    }
  };

  useEffect(() => {
    setCuser(user);
    window.addEventListener('scroll', checkOffset);

    return () => {
      window.removeEventListener('scroll', checkOffset);
    };
  }, []);

  useEffect(() => {
    setNavbarOpen(false);
  }, []);
  const blogPageLayout = 'Tinklaraštis';
  const blogPage = infoPages?.items?.find(
    (node) => node.layout === blogPageLayout
  );
  const smallLinksLayouts = [
    'Kontaktai',
    'Informacinis puslapis',
    'DUK puslapis',
  ];

  const navItems =
    infoPages?.items?.filter(
      (item) => !smallLinksLayouts.includes(item.layout)
    ) || [];

  const smallNavItems =
    infoPages?.items?.filter((item) =>
      smallLinksLayouts.includes(item.layout)
    ) || [];

  return (
    <Box
      as="nav"
      className={`navbar ${
        theme === themes.light ? themes.light : themes.dark
      }`}
    >
      {supportBanner}
      <Container p={[3, null, null, 4]}>
        <Flex>
          <Link
            sx={{
              marginRight: 'auto',
              display: 'flex',
              alignItems: 'center',
              maxWidth: ['112px', '160px'],
            }}
            to={`/${homePageSlug}`}
            className="navbar__logo"
          >
            {theme === themes.light ? <LogoWhite /> : <LogoPrimary />}
          </Link>

          <Box
            sx={{
              display: ['none', null, null, 'flex'],
              flexDirection: 'row',
            }}
          >
            {blogPage && (
              <Link
                sx={{ marginLeft: 4 }}
                to={`/${blogPage?.slug}`}
                className="navbar__item"
              >
                {blogPage?.title}
              </Link>
            )}

            <Box sx={{ marginLeft: 4 }} className="navbar__item">
              <Dropdown
                customToggle={
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '10px',
                      alignItems: 'center',
                    }}
                    onClick={toggleSubjectsMenu}
                  >
                    {t('subjects')} <DropdownIcon className="drop-icon" />
                  </Box>
                }
                listClassName="navbar__subjects-list-arrow"
              >
                {' '}
                <Box
                  sx={{
                    fill: '#fff',
                    stroke: '#fff',
                    strokeWidth: '5px',
                    position: 'absolute',
                    top: '35px',
                    right: '50%',
                    marginRight: '-25px',
                  }}
                >
                  <svg width="50" height="20" viewBox="-5 -5 70 20">
                    <polygon strokeLinejoin="round" points="0,40 30,0 60,40" />
                  </svg>
                </Box>
              </Dropdown>
            </Box>

            <Link
              sx={{ marginLeft: 4 }}
              to={`/${resolveTeachersSlug(pageLocale)}`}
              className="navbar__item"
            >
              {t('findATeacher')}
            </Link>
          </Box>

          <Flex sx={{ display: [null, null, null, 'flex'] }}>
            {cUser?.email && <CartButton className="navbar__item" />}
          </Flex>

          <Flex sx={{ display: ['none', null, null, 'flex'] }}>
            <LoginButton onLoginIn={onLoginIn} />
          </Flex>

          <Box
            className="navbar__item"
            sx={{
              display: ['none', null, null, 'flex'],
            }}
          >
            <LanguageSwitcher
              variant="flags"
              pageLocale={pageLocale}
              languages={languages}
            />
          </Box>

          <Button
            p={2}
            variant="empty"
            sx={{ marginLeft: 4 }}
            onClick={handleNavbarToggle}
            className="navbar__burger"
            aria-label={t('toggleMenu')}
          >
            <BurgerIcon />
          </Button>
        </Flex>

        <Box
          mx={4}
          sx={{
            marginTop: '-20px',
            display: ['none', null, null, 'flex'],
            maxHeight: '35rem',
          }}
          className={subjectsOpen ? 'navbar__sublist open' : 'navbar__sublist'}
          onClick={(e) => e.stopPropagation()}
        >
          {subjects.map(({ name, slug, icon, count }) => (
            <Option key={name}>
              <Box
                sx={{
                  display: 'Flex',
                  alignItems: 'center',
                  gap: '20px',
                }}
                onClick={closeSubjectMenu}
              >
                <Link
                  sx={{
                    fontSize: 3,
                    textDecoration: 'none',
                    display: 'Flex',
                    alignItems: 'center',
                    transition: 'color 200ms ease-in-out',
                    color: 'text',
                    '&:hover': {
                      color: 'label',
                    },
                  }}
                  to={`/${slug}`}
                >
                  <Image
                    sx={{
                      height: '100%',
                      borderRadius: '50%',
                      marginRight: '20px',
                      transform: 'translateZ(0)',
                    }}
                    image={icon}
                    alt=""
                  />{' '}
                  {name}{' '}
                  <span
                    sx={{
                      color: 'label',
                      marginLeft: '6px',
                    }}
                  >
                    {count && `(${count})`}
                  </span>
                </Link>
              </Box>
            </Option>
          ))}

          <Box
            sx={{
              color: 'fieldBorder',
              width: '110px',
              height: '130px',
              position: 'absolute',
              bottom: '60px',
              right: '7%',
            }}
          >
            <LogoIcon sx={{ width: '100%', height: '100%' }} />
          </Box>
        </Box>

        <Box
          className={
            navbarOpen ? 'navbar__side-menu active' : 'navbar__side-menu'
          }
        >
          <Flex
            sx={{
              flexDirection: 'column',
              maxWidth: '90%',
              width: '400px',
              marginLeft: 'auto',
              backgroundColor: 'white',
              padding: '32px 24px',
              height: '100%',
            }}
          >
            <Box>
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  marginLeft: 3,
                }}
              >
                <Box
                  sx={{
                    display: ['block', 'block', 'block', 'none'],
                  }}
                >
                  <LanguageSwitcher
                    variant="flags"
                    pageLocale={pageLocale}
                    languages={languages}
                  />
                </Box>
                <Button
                  variant="empty"
                  onClick={closeNavbar}
                  sx={{
                    margin: ['10px 24px 10px auto', null, null, '24px 10px'],
                    fontSize: 2,
                    color: 'text',
                  }}
                >
                  {t('close')}
                </Button>
              </Box>
            </Box>
            <Box sx={{ overflow: 'auto' }}>
              <Link
                to={`/${resolveTeachersSlug(pageLocale)}`}
                className="navbar__item"
              >
                {t('findATeacher')}
              </Link>
              {navItems.map(({ slug, title }) => {
                return (
                  <Link key={slug} to={`/${slug}`} className="navbar__item">
                    {title}
                  </Link>
                );
              })}

              <hr className="navbar__item-hr" />

              {smallNavItems.map(({ slug, title }) => {
                return (
                  <Link
                    key={slug}
                    to={`/${slug}`}
                    className="navbar__item navbar__item--small"
                  >
                    {title}
                  </Link>
                );
              })}
            </Box>
            <Flex
              sx={{
                marginTop: ['auto', null, null, '0'],
              }}
            >
              <LoginButton onLoginIn={onLoginIn} />
            </Flex>
          </Flex>
        </Box>
      </Container>
    </Box>
  );
};

export default Navbar;
